import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeadingBackButton } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import {
  useInstitutionDetailsLocked,
  useInstitutionDetailsMutation,
  useInstitutionDetailsV2Answers,
  useInstitutionDetailsVersion,
} from '@/features/institution/hooks/useInstitutionDetails';
import { useInstitutionOnboardingNextDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingNextDetailsRoute';
import { useInstitutionOnboardingPrevDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingPrevDetailsRoute';
import { type InstitutionProducts, institutionProductsLeiNumberSchema, institutionProductsToInstitutionDetailsProductsApi } from '@/features/institution/products/schema';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { institutionDetailsV2ProductsSchema } from '@/services/api/profile';
import type { FormStep } from '@/types/components';
import { InstitutionProductsFieldsetFutures, InstitutionProductsFieldsetFuturesLegend } from './fieldsets/futures';
import { InstitutionProductsFieldsetLeiNumber, InstitutionProductsFieldsetLeiNumberLegend } from './fieldsets/lei-number';

const FORM_STEPS = {
  FUTURES: {
    Component: InstitutionProductsFieldsetFutures,
    Legend: InstitutionProductsFieldsetFuturesLegend,
    schema: institutionDetailsV2ProductsSchema,
  },
  LEI_NUMBER: {
    Component: InstitutionProductsFieldsetLeiNumber,
    Legend: InstitutionProductsFieldsetLeiNumberLegend,
    schema: institutionProductsLeiNumberSchema,
  },
} satisfies Record<string, FormStep<InstitutionProducts>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionProductsFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionProducts>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'products', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <div className="animate-in fade-in" key={currentStep}>
      <Step />
    </div>
  );
};
InstitutionProductsFormCurrentStep.displayName = 'InstitutionProductsFormCurrentStep';

const InstitutionProductsFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionProducts>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionProductsFormCurrentStepLegend.displayName = 'InstitutionProductsFormCurrentStepLegend';

const InstitutionProductsFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionProducts>();
  const prevDetailsRoute = useInstitutionOnboardingPrevDetailsRoute('products');
  const navigate = useNavigate();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate(prevDetailsRoute, { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, prevStep, navigate, prevDetailsRoute]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionProductsFormBackButton.displayName = 'InstitutionProductsFormBackButton';

const InstitutionProductsFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { nextStep } = useGuardedFormStepper<InstitutionProducts>();
  const { t } = useTranslation();

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionProductsFormFooter.displayName = 'InstitutionProductsFormFooter';

type InstitutionProductsFormFieldsetProps = {
  isPending: boolean;
  onGoToReview: (data: InstitutionProducts) => Promise<void>;
};

const InstitutionProductsFormFieldset = ({ isPending, onGoToReview }: InstitutionProductsFormFieldsetProps) => {
  const { currentStep } = useGuardedFormStepper<InstitutionProducts>();
  const isFuturesStep = STEPS[currentStep] === FORM_STEPS.FUTURES.Component;

  return (
    <fieldset className={isFuturesStep ? 'max-w-xl mx-auto' : undefined}>
      <InstitutionProductsFormCurrentStepLegend />
      <div className="flex flex-row justify-between -md:justify-end">
        <InstitutionProductsFormBackButton />
        <InstitutionGoToReviewStepperButton onSubmit={onGoToReview} />
      </div>
      {isFuturesStep ? (
        <InstitutionProductsFormCurrentStep />
      ) : (
        <PageCard size="lg" bordered fixedFooter>
          <PageCardContent className="pt-8">
            <InstitutionProductsFormCurrentStep />
          </PageCardContent>
          <InstitutionProductsFormFooter isPending={isPending} />
        </PageCard>
      )}
    </fieldset>
  );
};
InstitutionProductsFormFieldset.displayName = 'InstitutionProductsFormFieldset';

const InstitutionProductsForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { locked } = useInstitutionDetailsLocked();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'products');
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const getNextDetailsRoute = useInstitutionOnboardingNextDetailsRoute('products');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const onSubmit = async (data: InstitutionProducts, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    const company = institutionDetailsAnswers?.company;

    try {
      // V2 saves one section at a time, so a new or corrected LEI goes to the company section before products
      if (data.futures === 'YES' && company && data.leiNumber !== company.leiNumber) {
        await mutateAsync({ company: { ...company, leiNumber: data.leiNumber } });
      }

      const savedAnswers = { products: await institutionProductsToInstitutionDetailsProductsApi(data) };

      await mutateAsync(savedAnswers);
      navigate(isReturningToReview ? `${onboardingPath}/review` : getNextDetailsRoute(savedAnswers), { replace: true });
    } catch {
      toast({
        description: t('fundamentals.defaultError.message'),
        variant: 'destructive',
      });
    }
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = { futures: locked.products?.futures ?? institutionDetailsAnswers?.products?.futures, leiNumber: institutionDetailsAnswers?.company?.leiNumber ?? '' };

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <InstitutionProductsFormFieldset isPending={isPending} onGoToReview={(data) => onSubmit(data, { isReturningToReview: true })} />
    </GuardedFormStepper>
  );
};
InstitutionProductsForm.displayName = 'InstitutionProductsForm';

export default InstitutionProductsForm;
